import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Smartphone, QrCode, Star, Instagram, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

interface DemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DemoModal = ({ open, onOpenChange }: DemoModalProps) => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);

  const steps = [
    {
      id: "scan",
      icon: QrCode,
      title: language === "fr" ? "1. Le client scanne le QR code" : "1. Customer scans the QR code",
      description: language === "fr" 
        ? "Un simple scan depuis la table, le comptoir ou l'emballage." 
        : "A simple scan from the table, counter, or packaging.",
      mockup: (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 w-64 h-[420px] mx-auto shadow-2xl relative overflow-hidden">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
          <div className="mt-8 flex flex-col items-center justify-center h-full pb-8">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4, type: "spring" }}
              className="w-32 h-32 bg-white rounded-2xl p-3 shadow-lg mb-4"
            >
              <div className="w-full h-full bg-gradient-to-br from-rose-500 to-violet-500 rounded-lg flex items-center justify-center">
                <QrCode className="w-16 h-16 text-white" />
              </div>
            </motion.div>
            <motion.p 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              className="text-white/80 text-sm text-center px-4"
            >
              {language === "fr" ? "Scannez pour découvrir votre expérience" : "Scan to discover your experience"}
            </motion.p>
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
              className="mt-4 w-12 h-12 rounded-full bg-gradient-to-r from-rose-500 to-violet-500 flex items-center justify-center animate-pulse"
            >
              <Smartphone className="w-6 h-6 text-white" />
            </motion.div>
          </div>
        </div>
      )
    },
    {
      id: "welcome",
      icon: Heart,
      title: language === "fr" ? "2. Page d'accueil personnalisée" : "2. Personalized welcome page",
      description: language === "fr" 
        ? "Le client arrive sur une page aux couleurs de votre marque." 
        : "Customer lands on a branded welcome page.",
      mockup: (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 w-64 h-[420px] mx-auto shadow-2xl relative overflow-hidden">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
          <div className="mt-10 bg-white rounded-2xl mx-2 h-[340px] overflow-hidden">
            <motion.div 
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="h-24 bg-gradient-to-r from-rose-500 to-violet-500 flex items-center justify-center"
            >
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg"
              >
                <Heart className="w-8 h-8 text-rose-500" />
              </motion.div>
            </motion.div>
            <div className="p-4 text-center">
              <motion.h3 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="font-bold text-slate-800 text-lg"
              >
                Café Parisien
              </motion.h3>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-slate-500 text-xs mt-1"
              >
                {language === "fr" ? "Merci de votre visite !" : "Thanks for visiting!"}
              </motion.p>
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.3 }}
                className="mt-4 space-y-2"
              >
                <div className="bg-gradient-to-r from-rose-500 to-violet-500 text-white py-2 px-4 rounded-full text-sm font-medium">
                  {language === "fr" ? "Nous soutenir" : "Support us"}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "review",
      icon: Star,
      title: language === "fr" ? "3. Laissez un avis Google" : "3. Leave a Google review",
      description: language === "fr" 
        ? "Accès direct à Google Reviews avec un texte suggéré." 
        : "Direct access to Google Reviews with suggested text.",
      mockup: (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 w-64 h-[420px] mx-auto shadow-2xl relative overflow-hidden">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
          <div className="mt-10 bg-white rounded-2xl mx-2 h-[340px] overflow-hidden p-4">
            <motion.div 
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="flex items-center gap-3 mb-4"
            >
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">G</span>
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Google Reviews</p>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3 + i * 0.1, type: "spring" }}
                    >
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              className="bg-slate-100 rounded-lg p-3 text-xs text-slate-600 mb-3"
            >
              "{language === "fr" ? "Excellent café, service impeccable et ambiance chaleureuse !" : "Excellent coffee, impeccable service and warm atmosphere!"}"
            </motion.div>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-xs text-slate-400 mb-3"
            >
              {language === "fr" ? "Texte suggéré - modifiable" : "Suggested text - editable"}
            </motion.p>
            <motion.button 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
              className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm font-medium"
            >
              {language === "fr" ? "Publier l'avis" : "Post review"}
            </motion.button>
          </div>
        </div>
      )
    },
    {
      id: "instagram",
      icon: Instagram,
      title: language === "fr" ? "4. Suivez-nous sur Instagram" : "4. Follow us on Instagram",
      description: language === "fr" 
        ? "Lien direct vers votre profil Instagram pour fidéliser." 
        : "Direct link to your Instagram profile for loyalty.",
      mockup: (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 w-64 h-[420px] mx-auto shadow-2xl relative overflow-hidden">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
          <div className="mt-10 bg-white rounded-2xl mx-2 h-[340px] overflow-hidden">
            <motion.div 
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="h-20 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center"
            >
              <Instagram className="w-10 h-10 text-white" />
            </motion.div>
            <div className="p-4 text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="w-16 h-16 mx-auto -mt-10 bg-white rounded-full border-4 border-white shadow-lg overflow-hidden"
              >
                <div className="w-full h-full bg-gradient-to-br from-rose-400 to-violet-400" />
              </motion.div>
              <motion.h3 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="font-bold text-slate-800 mt-2"
              >
                @cafeparisien
              </motion.h3>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-slate-500 text-xs"
              >
                2.4K {language === "fr" ? "abonnés" : "followers"}
              </motion.p>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-4 grid grid-cols-3 gap-1"
              >
                {[...Array(6)].map((_, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.7 + i * 0.05, type: "spring" }}
                    className="aspect-square bg-gradient-to-br from-slate-200 to-slate-300 rounded" 
                  />
                ))}
              </motion.div>
              <motion.button 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.3 }}
                className="mt-3 w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-lg text-sm font-medium"
              >
                {language === "fr" ? "Suivre" : "Follow"}
              </motion.button>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "success",
      icon: Heart,
      title: language === "fr" ? "5. Client fidélisé !" : "5. Customer engaged!",
      description: language === "fr" 
        ? "Vous avez gagné un ambassadeur de votre marque." 
        : "You've gained a brand ambassador.",
      mockup: (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 w-64 h-[420px] mx-auto shadow-2xl relative overflow-hidden">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4, type: "spring" }}
            className="mt-10 bg-gradient-to-br from-rose-500 to-violet-500 rounded-2xl mx-2 h-[340px] overflow-hidden flex flex-col items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 150 }}
              className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-4"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
              >
                <Heart className="w-10 h-10 text-white fill-white" />
              </motion.div>
            </motion.div>
            <motion.h3 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              className="text-white font-bold text-xl text-center mb-2"
            >
              {language === "fr" ? "Merci !" : "Thank you!"}
            </motion.h3>
            <motion.p 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.3 }}
              className="text-white/80 text-sm text-center"
            >
              {language === "fr" 
                ? "Votre soutien compte énormément pour nous." 
                : "Your support means the world to us."}
            </motion.p>
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
              className="mt-6 flex gap-2"
            >
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Star className="w-4 h-4 text-white fill-white" />
              </div>
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Instagram className="w-4 h-4 text-white" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      )
    }
  ];

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDotClick = (index: number) => {
    setDirection(index > currentStep ? 1 : -1);
    setCurrentStep(index);
  };

  const handleClose = () => {
    setCurrentStep(0);
    setDirection(0);
    onOpenChange(false);
  };

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-gradient-to-br from-slate-50 to-rose-50/30">
        <div className="p-6 pb-0">
          <DialogHeader>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
              >
                <DialogTitle className="text-2xl font-display text-slate-800 flex items-center gap-3">
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-500 to-violet-500 flex items-center justify-center"
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </motion.div>
                  {currentStepData.title}
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-base mt-2">
                  {currentStepData.description}
                </DialogDescription>
              </motion.div>
            </AnimatePresence>
          </DialogHeader>
        </div>

        <div className="p-8 overflow-hidden relative min-h-[480px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 },
              }}
              className="absolute"
            >
              {currentStepData.mockup}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-4">
          {steps.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => handleDotClick(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? "w-6 bg-gradient-to-r from-rose-500 to-violet-500" 
                  : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-white/50">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="text-slate-600"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {language === "fr" ? "Précédent" : "Previous"}
          </Button>

          <span className="text-sm text-slate-400">
            {currentStep + 1} / {steps.length}
          </span>

          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-gradient-to-r from-rose-500 to-violet-500 text-white hover:from-rose-400 hover:to-violet-400"
            >
              {language === "fr" ? "Suivant" : "Next"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              className="bg-gradient-to-r from-rose-500 to-violet-500 text-white hover:from-rose-400 hover:to-violet-400"
            >
              {language === "fr" ? "Fermer" : "Close"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
